import React, { useState } from 'react';
import { Form, Input, Typography, Upload, Tag, Row, Col, Tabs } from 'antd';
import { InfoCircleOutlined, UploadOutlined, PlusOutlined } from '@ant-design/icons';
import CustomTooltip from '../../Components/Modals/CustomTooltip';
import Tutorial from './Tutorial';

const { Title, Text } = Typography;
const { TextArea } = Input;
const InfoIcon = InfoCircleOutlined;

const SEOStep = ({ form, formData, handleFormChange, handleKeywordInputChange, handleKeywordInputConfirm, handlePreview, handleRemoveKeyword, keywordInputVisible, setKeywordInputVisible, keywordInputValue }) => {
    // Ensure formData.seo structure is initialized
    if (!formData.seo) {
        formData.seo = { keywords: [], metaDescriptions: {} };
    } else {
        if (!formData.seo.keywords) {
            formData.seo.keywords = [];
        }
        if (!formData.seo.metaDescriptions) {
            formData.seo.metaDescriptions = {};
        }
    }

    const keywordsLimit = 6;
    const supportedLanguages = [
        { key: 'it', label: 'IT', fullName: 'Italian' },
        { key: 'en', label: 'EN', fullName: 'English' },
        { key: 'fr', label: 'FR', fullName: 'French' },
        { key: 'es', label: 'ES', fullName: 'Spanish' },
        { key: 'de', label: 'DE', fullName: 'German' }
    ];

    const [activeLanguage, setActiveLanguage] = useState('it');

    // No need for manual handlers - Ant Design Form handles everything automatically

    return (
        <Row gutter={32} style={{ minHeight: 400 }}>
            {/* Left: Form */}
            <Col xs={24} md={12}>
                <div style={{ padding: '1.25rem 1rem' }}>
                    <Title level={4}>SEO Settings</Title>
                    <Text type="secondary">
                        Optimize your catalogue for search engines.
                    </Text>

                    <Form
                        form={form}
                        layout="vertical"
                        style={{ marginTop: '1.5rem' }}
                        onValuesChange={handleFormChange}
                    >
                        <Form.Item
                            name={['seo', 'title']}
                            label="SEO Title"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter SEO title',
                                },
                            ]}
                        >
                            <Input placeholder="Enter SEO title" />
                        </Form.Item>

                        <Form.Item
                            label={<>
                                Featured Image
                                <CustomTooltip title="Upload an image that will be shown when your catalogue is shared on social media">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Upload
                                listType="picture-card"
                                className="avatar-uploader"
                                showUploadList={false}
                                beforeUpload={() => false}
                                onChange={(info) => {
                                    if (info.file) {
                                        handleFormChange({
                                            seo: {
                                                ...formData.seo,
                                                featuredImage: info.file
                                            }
                                        });
                                    }
                                }}
                            >
                                {formData.seo.featuredImage ? (
                                    <img
                                        src={
                                            formData.seo.featuredImage instanceof File
                                                ? URL.createObjectURL(formData.seo.featuredImage)
                                                : formData.seo.featuredImage.url || formData.seo.featuredImage
                                        }
                                        alt="Featured"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div>
                                        <UploadOutlined />
                                        <div style={{ marginTop: 8 }}>Upload</div>
                                    </div>
                                )}
                            </Upload>
                        </Form.Item>

                        {/* Meta Descriptions - Multilingual */}
                        <Form.Item
                            label={<>
                                Meta Descriptions
                                <CustomTooltip title="Write compelling descriptions for each language to improve search engine visibility">
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <Tabs
                                activeKey={activeLanguage}
                                onChange={setActiveLanguage}
                                size="small"
                                items={supportedLanguages.map(lang => ({
                                    key: lang.key,
                                    label: lang.label,
                                    children: (
                                        <Form.Item
                                            name={['seo', 'metaDescriptions', lang.key]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <TextArea
                                                placeholder={`Enter meta description in ${lang.fullName}`}
                                                maxLength={170}
                                                showCount
                                                rows={4}
                                            />
                                        </Form.Item>
                                    )
                                }))}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<>
                                Keywords
                                <CustomTooltip title={`Maximum ${keywordsLimit} keywords`}>
                                    <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                </CustomTooltip>
                            </>}
                        >
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {Array.isArray(formData.seo.keywords) && formData.seo.keywords.map((keyword, index) => (
                                    <Tag
                                        key={index}
                                        closable
                                        onClose={() => handleRemoveKeyword(keyword)}
                                    >
                                        {keyword}
                                    </Tag>
                                ))}

                                {(!formData.seo.keywords || formData.seo.keywords.length < keywordsLimit) && (
                                    keywordInputVisible ? (
                                        <Input
                                            type="text"
                                            size="small"
                                            style={{ width: '6.25rem' }}
                                            value={keywordInputValue}
                                            onChange={handleKeywordInputChange}
                                            onBlur={handleKeywordInputConfirm}
                                            onPressEnter={handleKeywordInputConfirm}
                                            autoFocus
                                        />
                                    ) : (
                                        <Tag
                                            onClick={() => setKeywordInputVisible(true)}
                                            style={{ background: '#fff', borderStyle: 'dashed' }}
                                        >
                                            <PlusOutlined /> Add Keyword
                                        </Tag>
                                    )
                                )}
                            </div>
                        </Form.Item>
                    </Form>
                </div>
            </Col>
            {/* Right: Tutorial */}
            <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start' }}>
                <Tutorial
                    title="SEO Settings"
                    description={
                        <>
                            Improve how your catalogue appears in search engines and when shared.<br />
                            Set a title, meta descriptions in multiple languages, featured image, and relevant keywords to boost discoverability and click-through rates.<br /><br />
                            <b>Featured Image Guidelines:</b>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li><strong>Featured Image:</strong> 500 × 500 pixels (square), PNG/JPEG, max 10 MB</li>
                                <li>This image represents your catalogue in previews and social media shares</li>
                                <li>Ensure it remains clear and legible at small sizes</li>
                            </ul>
                            <br />
                            <b>Tips:</b>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li>Choose a clear, descriptive SEO title for your catalogue</li>
                                <li>Write concise meta descriptions (max 170 characters) for each language</li>
                                <li>Use a high-quality featured image for social sharing</li>
                                <li>Add up to 6 keywords that best describe your catalogue</li>
                                <li>Italian description is used as fallback for other languages</li>
                            </ul>
                        </>
                    }
                />
            </Col>
        </Row>
    );
};

export default SEOStep;
